// Hang-up flow for the Live Calls listing: which call is waiting for confirmation,
// the request itself, and its busy / success / error handling. UI-free so any board
// can reuse it with <HangupButton> (trigger) and <HangupDialog> (confirm).
import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { postApiCall } from '@/service/httpCall';
import { API_AGENT_MOBILE_HANGUP } from '@/service/url';
import type { LiveCall } from './types';
import { buildHangupBody } from './liveCallsModel';

export interface UseLiveCallHangupResult {
  /** Call waiting for confirmation; null when the dialog is closed. */
  target: LiveCall | null;
  busy: boolean;
  start: (call: LiveCall) => void;
  cancel: () => void;
  confirm: () => Promise<void>;
}

/** @param onDone called after the backend accepted the hang-up (refresh the feed). */
export function useLiveCallHangup(onDone?: () => void): UseLiveCallHangupResult {
  const [target, setTarget] = useState<LiveCall | null>(null);
  const [busy, setBusy] = useState(false);

  const start = useCallback((call: LiveCall) => setTarget(call), []);
  const cancel = useCallback(() => {
    if (!busy) setTarget(null);
  }, [busy]);

  const confirm = useCallback(async () => {
    if (!target || busy) return;
    setBusy(true);
    try {
      const res = await postApiCall(API_AGENT_MOBILE_HANGUP, buildHangupBody(target));
      toast.success(res?.already_ended ? 'Call already ended' : 'Hangup sent');
      setTarget(null);
      onDone?.();
    } catch (err) {
      toast.error(typeof err === 'string' && err ? err : 'Could not hang up the call');
    } finally {
      setBusy(false);
    }
  }, [target, busy, onDone]);

  return { target, busy, start, cancel, confirm };
}
